import {useTakeoverBusinessCaseMutation} from '@dms/api/sales';
import {handleApiError, useGetCurrentBranch} from '@dms/shared';

/**
 * Handles the logic for taking over a vehicle's business case from another branch.
 *
 * This hook encapsulates the business case takeover mutation, providing a simple
 * interface to trigger the action and track its loading state.
 *
 * @returns An object with the `handleTakeover` function and an `isLoading` flag.
 *
 * @example
 * ```tsx
 * const { handleTakeover, isLoading: isTakingOver } = useBusinessCaseTakeover();
 *
 * // In your component:
 * <Button
 *   onClick={() => handleTakeover(businessCaseId)}
 *   isLoading={isTakingOver}
 * >
 *   Takeover
 * </Button>
 * ```
 */
export function useBusinessCaseTakeover() {
  const [takeoverBusinessCase, {isLoading}] = useTakeoverBusinessCaseMutation();
  const {activeBranchId} = useGetCurrentBranch();

  /**
   * Initiates a business case takeover for a vehicle in another branch.
   * @param businessCaseId - The ID of the business case to take over.
   * @returns A promise that resolves on successful takeover.
   */
  const handleTakeover = (businessCaseId: string) => {
    if (!activeBranchId) {
      throw new Error('Cannot take over business case: Active branch ID is not available.');
    }

    return takeoverBusinessCase({
      businessCaseId,
      takeoverBusinessCaseRequestBody: {branchId: activeBranchId},
    })
      .unwrap()
      .catch(handleApiError);
  };

  return [handleTakeover, isLoading] as const;
}
